import { LLM, ModelProvider } from '@/types'

import { ANTHROPIC_LLM_LIST } from './anthropic-llm-list'
import { AWS_VOICE_LLM_LIST } from './aws-voice-llm-list'
import { BEDROCK_LLM_LIST } from './bedrock-llm-list'
import { GOOGLE_LLM_LIST } from './google-llm-list'
import { GROQ_LLM_LIST } from './groq-llm-list'
import { MARSHMALLOW_LLM_LIST } from './marshmallow-llm-list'
import { MISTRAL_LLM_LIST } from './mistral-llm-list'
import { OPENAI_LLM_LIST } from './openai-llm-list'
import { PERPLEXITY_LLM_LIST } from './perplexity-llm-list'

export const LLM_LIST: LLM[] = [
  ...MARSHMALLOW_LLM_LIST,
  ...ANTHROPIC_LLM_LIST,
  ...OPENAI_LLM_LIST,
  ...AWS_VOICE_LLM_LIST,
  ...BEDROCK_LLM_LIST,
  ...GOOGLE_LLM_LIST,
  ...MISTRAL_LLM_LIST,
  ...GROQ_LLM_LIST,
  ...PERPLEXITY_LLM_LIST
]

const resolveStringOrFunction = (value: string | (() => string) | undefined) =>
  typeof value === 'function' ? value() : value

/**
 * LLM.description (and Category.category/description) are `string | (() =>
 * string)` for lazy i18n resolution - fine for client-side rendering, but a
 * Server Component can't pass a function as a prop to a Client Component
 * ("Functions cannot be passed directly to Client Components"). Call this
 * before handing LLM_LIST (or any LLM[]) from a Server Component down to a
 * client child.
 */
export const resolveLlmForClient = (llm: LLM): LLM => ({
  ...llm,
  description: resolveStringOrFunction(llm.description),
  categories: llm.categories?.map((category) => ({
    ...category,
    category: resolveStringOrFunction(category.category) ?? '',
    description: resolveStringOrFunction(category.description)
  }))
})

const MODEL_PROVIDER_PREFIXES: ReadonlyArray<readonly [string, ModelProvider]> = [
  ['gpt-', 'openai'],
  ['o1', 'openai'],
  ['o3', 'openai'],
  ['claude-', 'anthropic'],
  ['gemini-', 'google'],
  ['gemma-', 'google'],
  ['mistral-', 'mistral'],
  ['mixtral-', 'groq'],
  ['llama3-', 'groq'],
  ['sonar', 'perplexity'],
  ['amazon.nova-', 'aws']
]

export function resolveProviderForModel(model: string): ModelProvider | undefined {
  const normalizedModel = model.trim()
  const exactProvider = LLM_LIST.find(
    (llm) => llm.modelId === normalizedModel || llm.hostedId === normalizedModel
  )?.provider

  if (exactProvider) {
    return exactProvider
  }

  if (normalizedModel.includes('/')) {
    return 'openrouter'
  }

  for (const [prefix, provider] of MODEL_PROVIDER_PREFIXES) {
    if (normalizedModel.startsWith(prefix)) {
      return provider
    }
  }

  return undefined
}

export const LLM_LIST_MAP: Record<string, LLM[]> = {
  marshmallow: MARSHMALLOW_LLM_LIST,
  openai: OPENAI_LLM_LIST,
  azure: OPENAI_LLM_LIST,
  google: GOOGLE_LLM_LIST,
  mistral: MISTRAL_LLM_LIST,
  groq: GROQ_LLM_LIST,
  perplexity: PERPLEXITY_LLM_LIST,
  anthropic: ANTHROPIC_LLM_LIST,
  aws: AWS_VOICE_LLM_LIST,
  bedrock: BEDROCK_LLM_LIST
}
